import React, { useState, useEffect } from "react";
import PlotBackground from "../../assets/svg/PlotListBackground.svg";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { Typography, Box, Card, CardContent, Grid } from "@mui/material";

// Navigation
import BottomNav from "./BottomNavigation";
// Icon
import PrePlantingCardGroup from "../../components/plot_engagement/PrePlantingCardGroup";
import PlantingCardGroup from "../../components/plot_engagement/PlantingCardGroup";
import DayWiseActivity from "../../components/plot_engagement/DayWiseActivityCard";
import HarvestingActivityCard from "../../components/plot_engagement/HarvestingActivityCard";
import instance from "../../globals/axios";
import { getFarmerPlotId } from "../../globals/utils";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";

const useStyles = makeStyles((theme) => ({
  centerCard: {
    display: "flex",
    flexDirection: "column",
    minHeight: "92vh",
    background: `url(${PlotBackground}) center/cover no-repeat`,
    padding: "4px",
  },
  customCard: {
    width: "100%",
    minHeight: "180px",
    height: "70%",
    borderRadius: "10px",
    boxShadow: theme.shadows[3],
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "100%",
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "gray",
    margin: "0 5px",
    cursor: "pointer",
    border: "1px solid #FFF",
  },
  title: {
    fontSize: "24px !important",
    fontWeight: "700 !important",
  },
  text: {
    fontSize: "16px !important",
    fontWeight: "700 !important",
  },
}));

const fetchActivityData = async (farmerPlotId) => {
  try {
    const response = await instance(
      `plotengagement/get-overall-status?farmer_plot=${farmerPlotId}`
    );
    if (response) {
      const data = await response.data;
      return data;
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    console.error("Error fetching activity data:", error);
    return null;
  }
};

const Activity = () => {
  const classes = useStyles();
  const [prePlantingStatus, setPrePlantingStatus] = useState(null);
  const [plantingStatus, setPlantingStatus] = useState(null);
  const [dayWiseStatus, setDayWiseStatus] = useState(null);
  const [harvestingStatus, setHarvestingStatus] = useState(null);
  const [advisoryData, setAdvisoryData] = useState([]);
  const location = useLocation();
  let roles = "FARMER";
  const { t } = useTranslation();
  const fetchUpdatedStatus = async () => {
    try {
      const updatedStatus = await fetchActivityData(farmerPlotId);
      if (updatedStatus) {
        setPrePlantingStatus(updatedStatus.pre_planting);
        setPlantingStatus(updatedStatus.planting);
        setDayWiseStatus(updatedStatus.daywise_activity);
        setHarvestingStatus(updatedStatus.harvesting);
      }
    } catch (error) {
      console.error("Error fetching updated status:", error);
    }
  };
  let id = "";
  let variety = "";
  if (localStorage.getItem("farmerPlotId")) {
    variety = localStorage.getItem("plotVariety");
    id = localStorage.getItem("id");
  }
  let farmerPlotId = getFarmerPlotId();

  const getActivityTypeFromPath = () => {
    const currentPath = location.pathname;

    if (currentPath === "/activity/PrePlanting/") {
      return "PRE_PLANTING";
    } else if (currentPath === "/activity/planting-activity/") {
      return "PLANTING";
    } else if (currentPath === "/activity/daywise-activity/") {
      return "DAY_WISE_ACTIVITY";
    } else if (currentPath === "/activity/harvesting/") {
      return "HARVESTING";
    }
  };
  const fetchAdvisoryData = async (activityType) => {
    try {
      const response = await instance.get(
        `plotengagement/get-advisory?activity=${activityType}&variety=${variety}`
      );
      if (response && response.data && response.data.length > 0) {
        const data = response.data[0];
        const filteredAdvisories = data.main_advisory.filter(
          (advisory) => advisory.is_active
        );
        setAdvisoryData(filteredAdvisories);
      } else {
        setAdvisoryData([]);
        throw new Error("No advisory data found");
      }
    } catch (error) {
      console.error("Error fetching advisory data:", error);
    }
  };

  useEffect(() => {
    const activityType = getActivityTypeFromPath();
    fetchAdvisoryData(activityType);
  }, [location.pathname]);
  const [activeCard, setActiveCard] = useState(0);
  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchActivityData(farmerPlotId);
      if (data) {
        setPrePlantingStatus(data.pre_planting);
        setPlantingStatus(data.planting);
        setDayWiseStatus(data.daywise_activity);
        setHarvestingStatus(data.harvesting);
      }
    };

    fetchData();
  }, [farmerPlotId]);

  const handleDotClick = (index) => {
    if (index !== activeCard) {
      setActiveCard(index);
    }
  };

  const handleNext = () => {
    if (advisoryData.length > 0) {
      setActiveCard((prev) =>
        prev === advisoryData.length - 1 ? 0 : prev + 1
      );
    }
  };
  useEffect(() => {
    const intervalId = setInterval(handleNext, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, [advisoryData]);

  const getActiveCardContent = () => {
    const validAdvisoryData = advisoryData.filter(
      (advisory) => advisory.title && advisory.description && advisory.is_active
    );

    if (
      validAdvisoryData.length > 0 &&
      activeCard >= 0 &&
      activeCard < validAdvisoryData.length
    ) {
      return (
        <div>
          <Typography style={{ fontSize: "14px", fontWeight: 700 }}>
            {t(validAdvisoryData[activeCard].title)}
          </Typography>
          <Typography style={{ fontSize: "14px", fontWeight: 500 }}>
            {t(validAdvisoryData[activeCard].description)}
          </Typography>
        </div>
      );
    } else {
      return (
        <div>
          <Typography
            style={{ fontSize: "14px", fontWeight: 700, textAlign: "center" }}
          >
            {t("No Advisory available")}
          </Typography>
        </div>
      );
    }
  };

  const getComponent = () => {
    const currentRoute = location.pathname;
    if (currentRoute === "/activity/PrePlanting/") {
      return (
        <PrePlantingCardGroup
          status={prePlantingStatus}
          updateStatus={fetchUpdatedStatus}
        />
      );
    } else if (currentRoute === "/activity/planting-activity/") {
      return (
        <PlantingCardGroup
          status={plantingStatus}
          updateStatus={fetchUpdatedStatus}
          t={t}
        />
      );
    } else if (currentRoute === "/activity/daywise-activity/") {
      return (
        <DayWiseActivity
          status={dayWiseStatus}
          plantingStatus={plantingStatus}
          updateStatus={fetchUpdatedStatus}
          t={t}
        />
      );
    } else if (currentRoute === "/activity/harvesting/") {
      return (
        <HarvestingActivityCard
          status={harvestingStatus}
          updateStatus={fetchUpdatedStatus}
          t={t}
        />
      );
    } else {
      return null;
    }
  };
  return (
    <div>
      <CustomAppBar />
      <div className={classes.centerCard}>
        <Box mt={12} marginLeft={1} className={classes.cardContainer}>
          <Grid container>
            <Grid item xs={4}>
              <Typography className={classes.text}>
                {t("Plot")} {roles === "AO" ? farmerPlotId : id}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.title}>
                {t("Scheduler")}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.text}> {t(variety)}</Typography>
            </Grid>
          </Grid>
          <Card className={classes.customCard}>
            <CardContent className={classes.cardContent}>
              {getActiveCardContent()}
              <div className={classes.dotsContainer}>
                {advisoryData.map((_, index) => (
                  <div
                    key={index}
                    className={classes.dot}
                    style={{
                      backgroundColor:
                        index === activeCard ? "#6BAB43" : "gray",
                      marginTop: "150px",
                      strokeWidth: "1px",
                      stroke: "#5EB9E0",
                      // color: "#6BAB43",
                      fill: index === activeCard ? "#6BAB43" : "#FFF",
                    }}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Grid container>{getComponent()}</Grid>
        </Box>
      </div>
      <BottomNav />
    </div>
  );
};

export default Activity;
